/* eslint-disable react/jsx-props-no-spreading */
import {debounce, throttle} from 'lodash';
import React, {ComponentType, FC, memo, PropsWithChildren, useCallback, useState} from 'react';
import {Subtract} from 'utility-types';

import {useEventHandler} from '../../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {ChannelModel} from '../../../../libs/shared/core/src/models/api/channelModel';
import {TeammateModel} from '../../../../libs/shared/core/src/models/api/teammateModel';
import {PushProps} from '../../../../libs/shared/core/src/services/push/pushContext';
import {ConversationModel} from '../../../../src/models/api/conversationModel';
import {ApiPrecogContinueResultModel, PrecogTypesEnum} from '../../../../src/models/api/precogModel';
import {stopPrecogAsync} from '../../../../src/services/precogs';
import {usePostApi} from '../../../helpers/hookHelpers';
import {emitDirectPrecogDeleted, emitDirectPrecogUpdated} from './precogDirect';

export enum PrecogUpdateTypesEnum {
  CONTINUE,
  STOP,
}

export function useMessagePrecog(
  conversation: ConversationModel | null,
  channel: ChannelModel,
  userId: number,
  emitDirectEvent: (roomName: string, eventName: string, data: any) => void,
) {
  return usePrecog(PrecogTypesEnum.MESSAGE, conversation, channel, userId, emitDirectEvent);
}

export function useCommentPrecog(
  conversation: ConversationModel | null,
  userId: number,
  emitDirectEvent: (roomName: string, eventName: string, data: any) => void,
) {
  return usePrecog(PrecogTypesEnum.COMMENT, conversation, null, userId, emitDirectEvent);
}

function usePrecog(
  type: PrecogTypesEnum,
  conversation: ConversationModel | null,
  channel: ChannelModel | null,
  userId: number,
  emitDirectEvent: (roomName: string, eventName: string, data: any) => void,
) {
  const {continuePrecogThrottled, continueDirectPrecogThrottled, stopPrecogDebounced, conflictLink} =
    usePrecogHelpers(type, userId, emitDirectEvent);

  const updatePrecog = useEventHandler((updateType: PrecogUpdateTypesEnum) => {
    if (updateType === PrecogUpdateTypesEnum.STOP) {
      stopPrecogDebounced.flush();
    } else {
      continueDirectPrecogThrottled(conversation, channel);
      continuePrecogThrottled(conversation, channel);
      stopPrecogDebounced(conversation);
    }
  });
  return {updatePrecog, conflictLink};
}

/*
 * HOC.
 */

/* Props needed to start and stop precogs. */
export interface MessagePrecogPublicProps {
  conversation: ConversationModel | null;
  channel: ChannelModel;
  user: TeammateModel;
  emitDirectEvent: (roomName: string, eventName: string, data: any) => void;
}

/* Props needed to start and stop precogs. */
export interface CommentPrecogPublicProps {
  conversation: ConversationModel | null;
  user: TeammateModel;
}

/* Props supplied by withPrecog. */
export interface PrecogProps {
  precogsType: PrecogTypesEnum;
  updatePrecog: (updateType: PrecogUpdateTypesEnum) => void;
  conflictLink?: string;
}

/* Props that children must have. */
type MessagePrecogChildProps = MessagePrecogPublicProps & PrecogProps & PushProps;
type CommentPrecogChildProps = CommentPrecogPublicProps & PrecogProps & PushProps;

export function withMessagePrecog<T extends MessagePrecogChildProps>(
  Component: ComponentType<PropsWithChildren<T>>,
): FC<PropsWithChildren<Subtract<T, PrecogProps>>> {
  return memo((props) => {
    const {updatePrecog, conflictLink} = useMessagePrecog(
      props.conversation,
      props.channel,
      props.user.id,
      props.emitDirectEvent,
    );
    return (
      <Component
        {...(props as any)}
        precogsType={PrecogTypesEnum.MESSAGE}
        updatePrecog={updatePrecog}
        conflictLink={conflictLink}
      />
    );
  });
}

export function withCommentPrecog<T extends CommentPrecogChildProps>(
  Component: ComponentType<PropsWithChildren<T>>,
): FC<PropsWithChildren<Subtract<T, PrecogProps>>> {
  return memo((props) => {
    const {updatePrecog, conflictLink} = useCommentPrecog(
      props.conversation,
      props.user.id,
      props.emitDirectEvent,
    );
    return (
      <Component
        {...(props as any)}
        precogsType={PrecogTypesEnum.COMMENT}
        updatePrecog={updatePrecog}
        conflictLink={conflictLink}
      />
    );
  });
}

/*
 * Helpers.
 */

function useStartPrecogAsync() {
  const startPrecogApi = usePostApi({
    apiType: ApiPrecogContinueResultModel,
    onStateChange: () => {},
  });

  return (conversation: ConversationModel, type: PrecogTypesEnum, channel: ChannelModel | null) => {
    const url = `${conversation.url}/precog/continue/${type}`;

    return startPrecogApi(url, channel ? {channel_id: channel.id} : undefined);
  };
}

function usePrecogHelpers(
  precogType: PrecogTypesEnum,
  userId: number,
  emitDirectEvent: (roomName: string, eventName: string, data: any) => void,
) {
  const startPrecogAsync = useStartPrecogAsync();
  const [conflictLink, setConflictLink] = useState<string | undefined>(undefined);

  const continuePrecogThrottled = useCallback(
    throttle(
      async (conversation: ConversationModel | null, channel: ChannelModel | null) => {
        if (!conversation) {
          return;
        }

        const result = await startPrecogAsync(conversation, precogType, channel);
        if (result) {
          setConflictLink(result.conflictLink);
        }
      },
      7000,
      {leading: true, trailing: false},
    ),
    [setConflictLink],
  );

  const continueDirectPrecogThrottled = useCallback(
    throttle(
      async (conversation: ConversationModel | null, channel: ChannelModel | null) => {
        if (!conversation) {
          return;
        }

        try {
          emitDirectPrecogUpdated(conversation.id, userId, precogType, emitDirectEvent);
        } catch (e) {
          // An exception is raised if the room is left before we're actually able to send the update.
        }
      },
      // Direct precog need to be updated more frequently because their updatedAt date isn't bumped by saving the comment.
      3000,
      {leading: true, trailing: false},
    ),
    [userId, precogType, emitDirectEvent],
  );

  const stopPrecogDebounced = useCallback(
    debounce(async (conversation: ConversationModel | null) => {
      continuePrecogThrottled.cancel();
      continueDirectPrecogThrottled.cancel();

      if (conversation) {
        try {
          emitDirectPrecogDeleted(conversation.id, userId, precogType, emitDirectEvent);
        } catch (e) {
          // An exception is raised if the room is left before we're actually able to send the update.
        }

        await stopPrecogAsync(conversation);
      }
    }, 5000),
    [continuePrecogThrottled],
  );

  return {continuePrecogThrottled, continueDirectPrecogThrottled, stopPrecogDebounced, conflictLink};
}
